import React, { useState, useEffect } from "react";
import apiClient from "../apiClient";
import { useShop } from "../context/ShopContext";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ResponsiveTable from "./ResponsiveTable";

/* ── helpers ── */
const fmtAmt = (v) => `₹${parseFloat(v) || 0}`;
const fmtDate = (d) => {
  if (!d) return "N/A";
  // Handle ISO string or plain date string
  const part = d.split("T")[0];
  return part.split("-").reverse().join("-");
};

/* ── Column definitions ── */
const COLUMNS = [
  { key: "sno",         label: "S. No.",       align: "center", width: 52 },
  { key: "expName",     label: "Expense Name" },
  { key: "shopName",    label: "Shop Name" },
  { key: "expenseDate", label: "Date",         render: (v) => fmtDate(v) },
  { key: "userName",    label: "User" },
  { key: "amount",      label: "Amount",       align: "right",  render: fmtAmt },
];

/* ── Mobile card renderer ── */
const ExpenseCard = ({ row, index }) => (
  <div className="rsp-card">
    <div className="rsp-card-header">
      <span className="rsp-card-title">{row.expName || "Expense"}</span>
      <span className="rsp-card-badge">#{index + 1}</span>
    </div>
    <div className="rsp-card-body">

      <div className="rsp-stat-grid rsp-stat-grid-2" style={{ marginBottom: 7 }}>
        <div className="rsp-stat">
          <span className="rsp-stat-label">Shop</span>
          <span className="rsp-stat-value">{row.shopName || "—"}</span>
        </div>
        <div className="rsp-stat">
          <span className="rsp-stat-label">Date</span>
          <span className="rsp-stat-value">{fmtDate(row.expenseDate)}</span>
        </div>
      </div>

      <div className="rsp-stat" style={{ textAlign: "left", marginBottom: 0 }}>
        <span className="rsp-stat-label">By</span>
        <span className="rsp-stat-value">{row.userName || "—"}</span>
      </div>

      <div className="rsp-card-accent">
        <span className="rsp-card-accent-label">Amount</span>
        <span className="rsp-card-accent-value">{fmtAmt(row.amount)}</span>
      </div>
    </div>
  </div>
);

/* ── Mobile totals card ── */
const ExpenseTotalsCard = ({ data }) => {
  const total = data.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0).toFixed(2);
  return (
    <div className="rsp-totals-card">
      <div className="rsp-totals-title">Grand Total</div>
      <div className="rsp-totals-accent">
        <span className="rsp-totals-accent-label">Total Expense</span>
        <span className="rsp-totals-accent-value">₹{total}</span>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
const Report = () => {
  const { shopId, token } = useShop();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const showToast = (message, type = "error") => {
    toast.dismiss();
    if (type.toLowerCase() === "error") toast.error(message);
    else if (type.toLowerCase() === "success") toast.success(message);
  };

  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        setLoading(true);
        const response = await apiClient.post("/Liquor/GetExpenseReport", { shopId });
        if (response.data && response.data.code === 200) {
          setExpenses(response.data.data || []);
        } else {
          setError("Unexpected response format.");
        }
      } catch (err) {
        console.error("Error fetching expense report:", err);
        setError("Unable to fetch expense data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    if (shopId && token) fetchExpenses();
  }, [shopId, token]);

  const exportToExcel = () => {
    if (!expenses || expenses.length === 0) {
      showToast("No data to export!", "error");
      return;
    }
    try {
      const worksheet = XLSX.utils.json_to_sheet(
        expenses.map((expense, index) => ({
          "S. No.": index + 1,
          "Amount": expense.amount,
          "Shop Name": expense.shopName,
          "Expense Name": expense.expName,
          "Expense Date": fmtDate(expense.expenseDate),
          "User Name": expense.userName,
        }))
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Expense Report");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const isOldIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.Promise;
      if (isOldIOS) {
        const reader = new FileReader();
        reader.onload = function () {
          const link = document.createElement("a");
          link.href = reader.result;
          link.download = "Expense_Report.xlsx";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        };
        reader.readAsDataURL(data);
      } else {
        saveAs(data, "Expense_Report.xlsx");
      }
      showToast("Data exported successfully!", "success");
    } catch (error) {
      console.error("Error exporting data:", error);
      showToast("Failed to export data.", "error");
    }
  };

  /* Add serial number to data */
  const tableData = expenses.map((e, i) => ({ ...e, sno: i + 1 }));

  /* Footer totals */
  const footerTotals = expenses.length > 0 ? {
    sno: "",
    expName: "",
    shopName: "",
    expenseDate: "",
    userName: "",
    amount: `₹${expenses.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0).toFixed(2)}`,
  } : null;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <ToastContainer />

      <div className="d-flex flex-column flex-column-fluid">
        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-gray-900 fw-bold fs-3 flex-column justify-content-center my-0">
                Expense Report
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="index.html" className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-500 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Reports</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-fluid">
            <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
              <div className="col-xl-12">
                <div className="card card-flush">
                  <div className="card-body pt-6">

                    {/* Export button */}
                    <div style={{ marginBottom: 16 }}>
                      <button
                        onClick={exportToExcel}
                        className="btn btn-success"
                        style={{ width: "100%", maxWidth: 200 }}
                      >
                        Export to Excel
                      </button>
                    </div>

                    {error && (
                      <p style={{ color: "red", marginBottom: 12 }}>{error}</p>
                    )}

                    <ResponsiveTable
                      columns={COLUMNS}
                      data={tableData}
                      loading={loading}
                      footer={footerTotals}
                      footerLabel="Total"
                      emptyMessage="No expenses found."
                      maxHeight="calc(100vh - 280px)"
                      renderCard={(row, i) => <ExpenseCard key={row.id || i} row={row} index={i} />}
                      renderTotalsCard={expenses.length > 0 ? () => <ExpenseTotalsCard data={expenses} /> : undefined}
                    />

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="kt_app_footer" className="app-footer">
        <div className="app-container container-fluid d-flex flex-column flex-md-row flex-center flex-md-stack py-3">
          <div className="text-gray-900 order-2 order-md-1">
            <span className="text-muted fw-semibold me-1">2025&copy;</span>
            <a href="#" className="text-gray-800 text-hover-primary">WineShop</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Report;
